import { batch, signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */

/**
 * A class for managing the lifecycle of a promise. Given an asynchronous function, it exposes a
 * function that wraps it (`invoke`) and updates the `loading`, `error` and `data` members to
 * reflect the state of the promise.
 *
 * @template T
 * @template {any[]} U
 */
export class PromiseSignal {
	/** @type {(...args: U) => Promise<T>} */
	#fn;

	#loading = signal(false);
	get loading() {
		return this.#loading.value;
	}

	/** @type {Signal<Error | undefined>} */
	#error = signal();
	get error() {
		return this.#error.value;
	}

	/** @type {Signal<T | undefined>} */
	#data = signal();
	get data() {
		return this.#data.value;
	}
	set data(data) {
		this.#data.value = data;
	}

	#success = signal(false);
	get success() {
		return this.#success.value;
	}

	/**
	 * @param {(...args: U) => Promise<T>} fn
	 * @param {T} [initial]
	 */
	constructor(fn, initial) {
		this.#fn = fn;

		if (initial !== undefined) this.#data.value = initial;
	}

	/**
	 * @param {U} args
	 * @returns {Promise<T>}
	 */
	invoke = async (...args) => {
		if (this.#loading.value) throw new Error("Promise is already pending.");
		this.#loading.value = true;

		try {
			const result = await this.#fn(...args);
			batch(() => {
				this.#loading.value = false;
				this.#error.value = undefined;
				this.#data.value = result;
				this.#success.value = true;
			});

			return result;
		} catch (e) {
			batch(() => {
				this.#loading.value = false;
				this.#error.value = e instanceof Error ? e : new Error(`${e}`);
				this.#data.value = undefined;
				this.#success.value = false;
			});
			throw e;
		}
	};

	reset = () => {
		if (this.#loading.peek()) return;
		batch(() => {
			this.#data.value = undefined;
			this.#error.value = undefined;
			this.#success.value = false;
		});
	};
}
